import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Eye, Users, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// These roles always see every file, so they are shown ticked and locked.
export const ALWAYS_SEE_ROLES = ['superadmin', 'admin'];
// Uploads by these roles are always visible to everyone (no selector).
export const UNRESTRICTED_UPLOADER_ROLES = ['user', 'employee'];

// Roles are fetched fresh for each picker so roles added or removed mid-session show up.
export const useRoles = () => {
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/roles`).then((r) => {
      if (alive) setRoles((r.data || []).map((x) => ({ role: x.role, label: x.label || x.role })));
    }).catch(() => { if (alive) setRoles([]); });
    return () => { alive = false; };
  }, []);
  return roles;
};

/** Who can see a file: everyone (incl. roles added later) or a chosen set of roles. */
export const RoleVisibilityPicker = ({ value, onChange, testId = 'attachment-visibility' }) => {
  const { t } = useTranslation();
  const roles = useRoles();
  const picked = new Set(value.roles || []);
  const selectable = roles.filter((r) => !ALWAYS_SEE_ROLES.includes(r.role));
  const setRoles = (list) => onChange({ all: false, roles: list });
  const toggle = (role, on) => setRoles(on ? [...picked, role] : [...picked].filter((x) => x !== role));

  return (
    <div className="rounded-md border p-2 space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs">
          <p className="font-medium flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{t('fileVisibility.title')}</p>
          <p className="text-gray-500">{value.all ? t('fileVisibility.allHint') : t('fileVisibility.someHint')}</p>
        </div>
        <label className="flex items-center gap-2 text-xs whitespace-nowrap">
          {t('fileVisibility.everyone')}
          <Switch checked={value.all} onCheckedChange={(on) => onChange({ all: on, roles: on ? [] : selectable.map((r) => r.role) })}
            data-testid={`${testId}-all`} />
        </label>
      </div>
      {!value.all && (
        <>
          <div className="flex gap-1">
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRoles(selectable.map((r) => r.role))} data-testid={`${testId}-select-all`}>
              {t('fileVisibility.selectAll')}
            </Button>
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRoles([])} data-testid={`${testId}-select-none`}>
              {t('fileVisibility.selectNone')}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {roles.map((r) => {
              const locked = ALWAYS_SEE_ROLES.includes(r.role);
              return (
                <label key={r.role} className={`flex items-center gap-2 text-xs rounded px-1.5 py-1.5 ${locked ? 'text-gray-400' : 'hover:bg-slate-50 cursor-pointer'}`}>
                  <Checkbox checked={locked || picked.has(r.role)} disabled={locked}
                    onCheckedChange={(on) => toggle(r.role, !!on)} data-testid={`${testId}-role-${r.role}`} />
                  <span>{r.label}</span>
                  {locked && <span className="text-[10px]">({t('fileVisibility.always')})</span>}
                </label>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-500">{t('fileVisibility.uploaderAlways')}</p>
        </>
      )}
    </div>
  );
};

/** Badge on each file; opens the picker for the uploader / admins. */
export const FileVisibilityBadge = ({ attachment, onSave }) => {
  const { t } = useTranslation();
  const roles = useRoles();
  const legacy = attachment.visible_to_all === undefined;
  const [draft, setDraft] = useState({ all: attachment.visible_to_all !== false, roles: attachment.visible_roles || [] });
  const [open, setOpen] = useState(false);
  const labelOf = (role) => roles.find((r) => r.role === role)?.label || role;
  const summary = legacy
    ? (attachment.crew_visible ? t('auto.misc.sharedWithCrew') : t('auto.misc.private'))
    : attachment.visible_to_all
      ? t('fileVisibility.everyoneShort')
      : t('fileVisibility.rolesShort', { count: (attachment.visible_roles || []).length });
  const title = !legacy && !attachment.visible_to_all ? (attachment.visible_roles || []).map(labelOf).join(', ') : undefined;
  const badge = (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] text-slate-600" title={title}
      data-testid={`attachment-visibility-badge-${attachment.id}`}>
      <Users className="h-3 w-3" />{summary}
    </span>
  );
  if (!attachment.can_edit_visibility) return badge;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft({ all: attachment.visible_to_all !== false, roles: attachment.visible_roles || [] }); }}>
      <PopoverTrigger asChild>
        <button type="button" className="hover:opacity-80" data-testid={`attachment-visibility-edit-${attachment.id}`}>{badge}</button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(92vw,360px)] p-2 space-y-2" align="start">
        <RoleVisibilityPicker value={draft} onChange={setDraft} testId={`attachment-visibility-${attachment.id}`} />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>{t('fileVisibility.cancel')}</Button>
          <Button size="sm" onClick={async () => { await onSave(attachment.id, draft); setOpen(false); }}
            data-testid={`attachment-visibility-save-${attachment.id}`}>{t('fileVisibility.save')}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const isAdminUser = (u) => !!u && (u.is_superadmin || ALWAYS_SEE_ROLES.includes(String(u.role || '').toLowerCase()));

export const visibilityFromDefault = (defaults, category) => {
  const d = (defaults || {})[category];
  return d && d.all === false ? { all: false, roles: d.roles || [] } : { all: true, roles: [] };
};

/** Client-side version of the admin "limited to selected roles" filter. */
export const matchesRoleFilter = (att, filter) => {
  if (!filter.on) return true;
  if (att.visible_to_all !== false) return false;
  return !filter.roles.length || (att.visible_roles || []).some((r) => filter.roles.includes(r));
};

/** Admin: apply one visibility setting to several files. */
export const BulkVisibilityButton = ({ fileIds, legacyCount = 0, onDone, testId = 'bulk-visibility' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ all: true, roles: [] });
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(false);
  const apply = async () => {
    setWarn(false);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/attachments/bulk-visibility`, {
        file_ids: fileIds, visible_to_all: draft.all, visible_roles: draft.all ? [] : draft.roles,
      });
      toast.success(t('fileVisibility.bulkSaved', { count: r.data.matched }));
      setOpen(false);
      setDraft({ all: true, roles: [] });
      onDone?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('fileVisibility.saveFailed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!fileIds.length} data-testid={`${testId}-open`}>
          <Users className="h-3.5 w-3.5 mr-1" />{t('fileVisibility.bulkButton', { count: fileIds.length })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(92vw,360px)] p-2 space-y-2" align="start">
        <RoleVisibilityPicker value={draft} onChange={setDraft} testId={`${testId}-picker`} />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>{t('fileVisibility.cancel')}</Button>
          <Button size="sm" onClick={() => (legacyCount ? setWarn(true) : apply())} disabled={busy} data-testid={`${testId}-apply`}>
            {t('fileVisibility.bulkApply', { count: fileIds.length })}
          </Button>
        </div>
      </PopoverContent>
      <AlertDialog open={warn} onOpenChange={setWarn}>
        <AlertDialogContent data-testid={`${testId}-legacy-warning`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('fileVisibility.legacyWarnTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('fileVisibility.legacyWarnBody', { count: legacyCount, total: fileIds.length })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-legacy-cancel`}>{t('fileVisibility.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={apply} data-testid={`${testId}-legacy-confirm`}>{t('fileVisibility.legacyWarnConfirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Popover>
  );
};

/** Admin filter: only files limited to selected roles (optionally narrowed to some roles). */
export const RoleFilter = ({ value, onChange, testId = 'role-filter' }) => {
  const { t } = useTranslation();
  const roles = useRoles().filter((r) => !ALWAYS_SEE_ROLES.includes(r.role));
  const toggle = (role, on) => onChange({ on: true, roles: on ? [...value.roles, role] : value.roles.filter((x) => x !== role) });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant={value.on ? 'default' : 'outline'} className="h-8 text-xs" data-testid={`${testId}-open`}>
          <Filter className="h-3.5 w-3.5 mr-1" />
          {value.on ? t('fileVisibility.filterActive', { count: value.roles.length }) : t('fileVisibility.filterLabel')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 space-y-2" align="start">
        <label className="flex items-center justify-between gap-2 text-xs font-medium">
          {t('fileVisibility.filterLimited')}
          <Switch checked={value.on} onCheckedChange={(on) => onChange({ on, roles: on ? value.roles : [] })} data-testid={`${testId}-toggle`} />
        </label>
        {value.on && (
          <>
            <p className="text-[11px] text-gray-500">{t('fileVisibility.filterHint')}</p>
            {roles.map((r) => (
              <label key={r.role} className="flex items-center gap-2 text-xs px-1 py-1 hover:bg-slate-50 rounded cursor-pointer">
                <Checkbox checked={value.roles.includes(r.role)} onCheckedChange={(on) => toggle(r.role, !!on)} data-testid={`${testId}-role-${r.role}`} />
                {r.label}
              </label>
            ))}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
